/**
 * RadarSource registry (M003 / MULTI_REGION_RADAR.md). Config-driven contract
 * so the region-aware orchestration in app.ts never special-cases a
 * particular service by name — it only calls the active source's methods.
 * The US entries wrap the pre-existing wmsApi.ts/radarApi.ts I/O UNCHANGED
 * (D-004/D-005 behaviour, 104 pre-M003 tests guard this); eu-de-dwd wraps the
 * new dwdApi.ts module.
 */
import type { LonLatBounds } from "./mercator";
import { CONUS_BOUNDS, fetchTimeExtent, buildFrameImageUrl } from "./radarApi";
import { fetchAdvertisedTimes, buildWmsFrameUrl } from "./wmsApi";
import { dwdAvailableTimes, buildDwdFrameUrl, validateDwdFrame, DWD_BOUNDS } from "./dwdApi";
import {
  evaluateCandidate,
  isDuplicateOfNeighbor,
  computeFrameTimes,
  FRAME_COUNT,
  BLANK_FRAME_BUFFER,
  FRAME_STEP_MS,
  type CandidateEvaluation,
  type CandidateVerdict
} from "./frames";
import {
  REFLECTIVITY_LEGEND_STOPS,
  LEGEND_GRADIENT_CSS,
  LEGEND_SCALE_TITLE,
  LEGEND_SOURCE_NOTE,
  DWD_REFLECTIVITY_STOPS,
  DWD_LEGEND_GRADIENT_CSS,
  DWD_LEGEND_SCALE_TITLE,
  DWD_LEGEND_SOURCE_NOTE,
  DWD_LEGEND_UNIT,
  type LegendStop
} from "./legend";

export type RegionId = "us" | "eu-de";
export type SourceId = "us-ncep" | "us-arcgis" | "eu-de-dwd";

export interface LegendSpec {
  stops: LegendStop[];
  gradientCss: string;
  scaleTitle: string;
  sourceNote: string;
  /** Present only for sources that publish real classified thresholds with a physical unit (DWD "dBZ"); omitted for the US continuous composite. */
  unit?: string;
}

export interface FreshnessThresholds {
  freshMaxMs: number;
  agingMaxMs: number;
}

/**
 * One radar data source. `availableTimes`/`buildFrameUrl`/`validateFrame`
 * are the only I/O + decision surface the orchestrator (app.ts) touches;
 * everything else is static per-source configuration.
 */
export interface RadarSource {
  id: SourceId;
  region: RegionId;
  displayName: string;
  attribution: string;
  /** Raster bbox the fetched image covers AND the Leaflet ImageOverlay bounds — must match 1:1 (D-003 lesson). */
  overlayBounds: LonLatBounds;
  /** Map view bounds to fit/constrain the map to for this region — may be narrower than overlayBounds (§R3). */
  fitBounds: LonLatBounds;
  minZoom: number;
  maxZoom: number;
  legend: LegendSpec;
  cadenceMs: number;
  /** Rolling window UI target, e.g. ~96 min (US) / 2 h (EU) — a contract PARAMETER, not necessarily the source's full history. */
  historyWindowMs: number;
  freshness: FreshnessThresholds;
  availableTimes(signal?: AbortSignal): Promise<number[]>;
  buildFrameUrl(tMs: number, cacheBusterMs?: number): string;
  validateFrame(stats: CandidateEvaluation): CandidateVerdict;
}

const US_LEGEND: LegendSpec = {
  stops: REFLECTIVITY_LEGEND_STOPS,
  gradientCss: LEGEND_GRADIENT_CSS,
  scaleTitle: LEGEND_SCALE_TITLE,
  sourceNote: LEGEND_SOURCE_NOTE
};

/**
 * eu-de-dwd legend (M003): EXACT thresholds + colors from a live GetStyles
 * response (2026-07-19) — 17 visible classes 7→≥85 dBZ, verbatim. See the
 * provenance note above DWD_REFLECTIVITY_STOPS in src/lib/legend.ts.
 */
const DWD_LEGEND: LegendSpec = {
  stops: DWD_REFLECTIVITY_STOPS,
  gradientCss: DWD_LEGEND_GRADIENT_CSS,
  scaleTitle: DWD_LEGEND_SCALE_TITLE,
  sourceNote: DWD_LEGEND_SOURCE_NOTE,
  unit: DWD_LEGEND_UNIT
};

/**
 * evaluateCandidate() plus the mature-frame duplicate override the WMS
 * source needs (moved here unchanged from app.ts's former evaluateWmsCandidate).
 */
function validateUsNcepFrame(stats: CandidateEvaluation): CandidateVerdict {
  const verdict = evaluateCandidate(stats);
  const neighbor =
    stats.prevPixelCount !== undefined && stats.prevByteSize !== undefined
      ? { px: stats.prevPixelCount, bytes: stats.prevByteSize }
      : undefined;
  if (verdict === "accept" && isDuplicateOfNeighbor({ px: stats.pixelCount, bytes: stats.byteSize }, neighbor)) {
    return "defer"; // time-snapping duplicate — never presented as its own observation
  }
  return verdict;
}

/** ArcGIS fallback's "availableTimes": fetch the current extent, then the same synthetic grid the pre-M003 app used (extra BLANK_FRAME_BUFFER candidates so trailing server-side blanks can be dropped). */
async function arcgisAvailableTimes(signal?: AbortSignal): Promise<number[]> {
  const extent = await fetchTimeExtent(signal);
  return computeFrameTimes(extent.endMs, FRAME_COUNT + BLANK_FRAME_BUFFER, FRAME_STEP_MS);
}

export const SOURCES: Record<SourceId, RadarSource> = {
  "us-ncep": {
    id: "us-ncep",
    region: "us",
    displayName: "Radar USA",
    attribution: "Dane radaru: NOAA / National Weather Service",
    overlayBounds: CONUS_BOUNDS,
    fitBounds: CONUS_BOUNDS,
    minZoom: 3,
    maxZoom: 10,
    legend: US_LEGEND,
    cadenceMs: 2 * 60 * 1000,
    historyWindowMs: 96 * 60 * 1000,
    freshness: { freshMaxMs: 15 * 60 * 1000, agingMaxMs: 30 * 60 * 1000 },
    availableTimes: (signal) => fetchAdvertisedTimes(signal),
    buildFrameUrl: (t, cb) => buildWmsFrameUrl(t, undefined, cb),
    validateFrame: validateUsNcepFrame
  },
  "us-arcgis": {
    id: "us-arcgis",
    region: "us",
    displayName: "Radar USA",
    attribution: "Dane radaru: NOAA / National Weather Service",
    overlayBounds: CONUS_BOUNDS,
    fitBounds: CONUS_BOUNDS,
    minZoom: 3,
    maxZoom: 10,
    legend: US_LEGEND,
    cadenceMs: FRAME_STEP_MS,
    historyWindowMs: FRAME_COUNT * FRAME_STEP_MS,
    freshness: { freshMaxMs: 15 * 60 * 1000, agingMaxMs: 30 * 60 * 1000 },
    availableTimes: arcgisAvailableTimes,
    buildFrameUrl: (t, cb) => buildFrameImageUrl(t, undefined, cb),
    validateFrame: (stats) => evaluateCandidate(stats)
  },
  "eu-de-dwd": {
    id: "eu-de-dwd",
    region: "eu-de",
    displayName: "Radar EUROPA · Niemcy",
    attribution: "Dane radaru: Deutscher Wetterdienst (DWD)",
    overlayBounds: DWD_BOUNDS,
    // Narrower than overlayBounds on purpose (§R3): the raster covers a margin
    // around Germany, the map view fits/constrains to Germany itself.
    fitBounds: { west: 5.5, south: 47.0, east: 15.7, north: 55.1 },
    minZoom: 5,
    maxZoom: 10,
    legend: DWD_LEGEND,
    cadenceMs: 5 * 60 * 1000,
    historyWindowMs: 2 * 60 * 60 * 1000,
    freshness: { freshMaxMs: 10 * 60 * 1000, agingMaxMs: 30 * 60 * 1000 },
    // Local 5-min-grid computation between hourly capability re-syncs —
    // polls must not download the 850 KB GetCapabilities every 60 s.
    availableTimes: (signal) => dwdAvailableTimes(2 * 60 * 60 * 1000, signal),
    buildFrameUrl: (t, cb) => buildDwdFrameUrl(t, cb),
    validateFrame: validateDwdFrame
  }
};

/** Primary → fallback source ids within a region (US only has a fallback; EU has a single source, D-006). */
export const REGION_SOURCE_IDS: Record<RegionId, SourceId[]> = {
  us: ["us-ncep", "us-arcgis"],
  "eu-de": ["eu-de-dwd"]
};

/** Pure: ordered (primary-first) sources for a region. */
export function sourcesForRegion(region: RegionId): RadarSource[] {
  return REGION_SOURCE_IDS[region].map((id) => SOURCES[id]);
}

export const REGION_STORAGE_KEY = "radar.region";

/**
 * Pure: resolve a raw localStorage value to a valid region, defaulting to
 * "us" for anything else (missing key, corrupted value, private-mode read
 * failure passed in as null) — DESIGN.md Aneks M003 §R1 "Persist".
 */
export function resolveStoredRegion(raw: string | null | undefined): RegionId {
  return raw === "eu-de" ? "eu-de" : "us";
}
